import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { SocialPlatform } from '@digital-twins/shared-types'

/**
 * The one post playing on the Topic page (plan D3): the boards and the post
 * pane share it, so starting another player unmounts the previous iframe.
 * Without a provider (the tracker drawer) each player keeps its own state.
 */
interface SocialPlayingPostValue {
  playingToken: string | null
  play: (postToken: string) => void
  stop: (postToken: string) => void
}

const SocialPlayingPostContext = createContext<SocialPlayingPostValue | null>(null)

interface SocialPlayingPostProviderProps {
  children: ReactNode
}

export function SocialPlayingPostProvider(props: SocialPlayingPostProviderProps) {
  const { children } = props

  const parent = useContext(SocialPlayingPostContext)
  const [playingToken, setPlayingToken] = useState<string | null>(null)

  const play = useCallback((postToken: string) => {
    setPlayingToken(postToken)
  }, [])
  const stop = useCallback((postToken: string) => {
    setPlayingToken((current) => (current === postToken ? null : current))
  }, [])
  const value = useMemo(() => ({ playingToken, play, stop }), [playingToken, play, stop])

  // A nested provider (the boards inside the Topic page) joins the outer one.
  if (parent) {
    return <>{children}</>
  }
  return <SocialPlayingPostContext.Provider value={value}>{children}</SocialPlayingPostContext.Provider>
}

interface SocialPlayingPost {
  isPlaying: boolean
  play: () => void
  stop: () => void
}

/**
 * Whether this post's player is mounted; the provider keeps one at a time.
 * `surface` tells two players of the same post apart (each board tile and the
 * post pane), so a post repeated across boards plays only where it was clicked.
 */
export function useSocialPlayingPost(postToken: string, surface?: string): SocialPlayingPost {
  const shared = useContext(SocialPlayingPostContext)
  const [localToken, setLocalToken] = useState<string | null>(null)

  const key = surface ? `${surface}:${postToken}` : postToken
  const sharedPlay = shared?.play
  const sharedStop = shared?.stop
  const play = useCallback(() => {
    if (sharedPlay) {
      sharedPlay(key)
    } else {
      setLocalToken(key)
    }
  }, [sharedPlay, key])
  const stop = useCallback(() => {
    if (sharedStop) {
      sharedStop(key)
    } else {
      setLocalToken(null)
    }
  }, [sharedStop, key])

  const isPlaying = shared ? shared.playingToken === key : localToken === key
  return { isPlaying, play, stop }
}

/**
 * The Play control laid over a cover: a small translucent scrim that steps
 * forward on hover or focus, so a board of videos does not read as a wall of
 * buttons. Callers add the size.
 */
export const SOCIAL_PLAY_BUTTON_CLASS =
  'absolute left-1/2 top-1/2 z-[2] flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-media-scrim/40 text-media-scrim-foreground opacity-80 ring-1 ring-media-scrim-foreground/20 backdrop-blur-sm transition duration-150 hover:bg-media-scrim/60 hover:opacity-100 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none'

/**
 * The Close control on a mounted player: back to the cover. A scrim chip with
 * a light ring, so it reads over a dark player header in either theme.
 */
export const SOCIAL_PLAYER_CLOSE_CLASS =
  'absolute left-2.5 top-2.5 z-[3] flex h-7 w-7 items-center justify-center rounded-full bg-media-scrim/60 text-media-scrim-foreground shadow-sm ring-1 ring-media-scrim-foreground/30 backdrop-blur-md transition hover:bg-media-scrim/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none'

/** The platform players the CSP `frame-src` allows (infra/docker/nginx/snippets). */
const SOCIAL_PLAYER_HOSTS: Record<SocialPlatform, string> = {
  youtube: 'www.youtube-nocookie.com',
  tiktok: 'www.tiktok.com',
  instagram: 'www.instagram.com',
  reddit: 'embed.reddit.com',
}

/** Players that start on load when asked: the click on Play is the explicit start. */
const SOCIAL_AUTOPLAY_PLATFORMS: Array<SocialPlatform> = ['youtube', 'tiktok']

/**
 * The official embed to mount after a click on Play, or null when the post
 * has none on its platform's player host. YouTube and TikTok start playing
 * right away (sound per the platform default).
 */
export function socialPlayerUrl(post: {
  platform: SocialPlatform
  embedUrl: string | null | undefined
}): string | null {
  const embedUrl = post.embedUrl ?? ''
  if (!embedUrl.startsWith(`https://${SOCIAL_PLAYER_HOSTS[post.platform]}/`)) {
    return null
  }
  if (!SOCIAL_AUTOPLAY_PLATFORMS.includes(post.platform) || /[?&]autoplay=/.test(embedUrl)) {
    return embedUrl
  }
  return `${embedUrl}${embedUrl.includes('?') ? '&' : '?'}autoplay=1`
}

/** The player frame's shape: YouTube landscape, TikTok and Reels portrait. */
export function socialPlayerAspectClass(platform: SocialPlatform): string {
  if (platform === 'youtube') {
    return 'aspect-video'
  }
  if (platform === 'reddit') {
    return 'aspect-[4/5]'
  }
  return 'aspect-[9/16]'
}
